import { useQuery } from '@tanstack/react-query'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { handleGetRequest } from '../../utils/api-handler.util'
import FavoriteButton from '../../components/Favorites/FavoritesButton'
import { useState } from 'react'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { Link } from 'react-router-dom'
import PageTitle from '../../components/common/PageTitle'
import ConfirmModal from '../../components/common/ConfirmModal'
import Pagination from '../../components/common/Pagination'
import DisplayShopName from '../../components/common/DisplayShopName'
import ShopLocation from '../../components/common/ShopLocation'
import SeoHelmet from '../../components/common/SeoHelmet'

export default function CustomerFavoriteRetailersPage() {
  const [currentPage, setCurrentPage] = useState(1)

  const { data: favoriteRetailersData, isLoading } = useQuery({
    queryKey: QueryKeys.favoriteRetailers(currentPage),
    queryFn: () => handleGetRequest(`/customers/favorites/retailers?page=${currentPage}`),
  })

  const [removedIds, setRemovedIds] = useState(new Set())
  const [pendingRemovalId, setPendingRemovalId] = useState(null)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const favoriteRetailers = favoriteRetailersData?.data || []
  const paginationMeta = favoriteRetailersData?.meta

  const handleToggle = (retailerId, newState) => {
    if (!newState) {
      setPendingRemovalId(retailerId)
      setIsConfirmModalOpen(true)
    }
  }

  const confirmRemoval = () => {
    setRemovedIds((prev) => new Set(prev).add(pendingRemovalId))
    setIsConfirmModalOpen(false)
    setPendingRemovalId(null)
    QueryKeys.favoriteRetailers.invalidate()
  }

  const cancelRemoval = () => {
    setIsConfirmModalOpen(false)
    setPendingRemovalId(null)
  }

  const visibleRetailers = favoriteRetailers.filter((retailer) => !removedIds.has(retailer.shopId))

  const renderRetailer = (retailer) => {
    return (
      <Link to={`/retailers/${retailer.shopSlug}/products`} key={retailer.shopId} className="block">
        <div className="flex items-center gap-4 p-4 bg-background rounded-lg shadow-md border border-secondary-200/30 m-2 hover:bg-neutral-200-50 transition">
          <img
            src={retailer?.shopPicture || ''}
            alt="Shop Picture"
            className="w-16 rounded-[10px] lg:w-24 aspect-square object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="font-medium font-fontFamily text-lg">
              <DisplayShopName
                shop={{
                  name: retailer?.shopName,
                  isPremium: retailer?.isPremium,
                  isVerified: retailer?.isApproved,
                }}
              />
            </p>
            <p className="font-fontFamily text-base  font-normal line-clamp-2">
              {retailer?.shopDescription}
            </p>
            <ShopLocation
              shop={{
                shopNumber: retailer.shopNumber,
                floor: retailer.floor,
                buildingName: retailer.buildingName,
              }}
              className="mt-1"
            />
          </div>
          <div className="ml-2">
            <FavoriteButton
              retailer={{
                id: retailer.shopId,
                shopSlug: retailer.shopSlug,
              }}
              isFavorite={true}
              onToggle={(newState) => handleToggle(retailer.shopId, newState)}
            />
          </div>
        </div>
      </Link>
    )
  }

  const render = () => {
    if (isLoading) {
      return <LoadingSpinner />
    }

    if (visibleRetailers.length === 0) {
      return (
        <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center">
          You do not have any favorite retailers yet.{' '}
          <Link to="/retailers" className="text-secondary-600  hover:underline">
            Browse Retailers
          </Link>
        </div>
      )
    }

    return (
      <div className="mx-auto mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {visibleRetailers.map((retailer) => renderRetailer(retailer))}
      </div>
    )
  }

  return (
    <div className="main-container min-h-screen">
      <SeoHelmet
        title="My Favorite Retailers"
        description="Manage your favorite retailers on Sokooni marketplace. Keep track of your preferred shops and access them quickly."
        keywords="favorite retailers, saved shops, bookmarked retailers, preferred sellers, Sokooni favorites"
        url="/favorites"
      />
      <PageTitle>Your Favorite Retailers</PageTitle>
      {render()}
      {paginationMeta && (
        <Pagination paginationMeta={paginationMeta} onPageChange={setCurrentPage} />
      )}

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={cancelRemoval}
        onConfirm={confirmRemoval}
        title={'Remove Favorite?'}
        message={`Are you sure you want to remove this retailer from your list of favorites?`}
        cancelText="Go Back"
      />
    </div>
  )
}
